import { useState, useEffect, useRef } from 'react';
import { useGetV1ProcessExportHistory } from '../api/generated/endpoint';
import { ProcessExportStatusEnum } from '../api/generated/models';

class PollingController {
    private listeners = new Set<() => void>();
    private isActiveFlag = false;

    start() {
        if (!this.isActiveFlag) {
            this.isActiveFlag = true;
            this.notify();
        }
    }

    stop() {
        if (this.isActiveFlag) {
            this.isActiveFlag = false;
            this.notify();
        }
    }

    isActive() {
        return this.isActiveFlag;
    }

    subscribe(callback: () => void) {
        this.listeners.add(callback);
        return () => {
            this.listeners.delete(callback);
        };
    }

    private notify() {
        this.listeners.forEach((callback) => callback());
    }
}

export const exportPollingController = new PollingController();

export function useExportPolling(
    options: {
        interval?: number;
        onExportComplete?: (exportFileName: string) => void;
    } = {}
) {
    const { interval = 10000, onExportComplete } = options;
    const [shouldPoll, setShouldPoll] = useState(
        exportPollingController.isActive()
    );
    const previousDataRef = useRef<any[]>([]);
    const manualStartTimeRef = useRef<number>(0);

    // Subscribe to polling controller
    useEffect(() => {
        const unsubscribe = exportPollingController.subscribe(() => {
            const isActive = exportPollingController.isActive();
            if (isActive) {
                // Record when polling was manually started
                manualStartTimeRef.current = Date.now();
            }
            setShouldPoll(isActive);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const { data, refetch } = useGetV1ProcessExportHistory({
        query: {
            enabled: true,
            refetchInterval: shouldPoll ? interval : false,
            refetchIntervalInBackground: false,
            structuralSharing: false,
            cacheTime: 0, // Don't cache the data
            staleTime: 0, // Consider data stale immediately
        },
    });

    // Manual polling fallback - in case refetchInterval doesn't work
    useEffect(() => {
        if (!shouldPoll) {
            return;
        }

        const intervalId = setInterval(() => {
            refetch();
        }, interval);

        return () => {
            clearInterval(intervalId);
        };
    }, [shouldPoll, interval, refetch]);

    // Check for newly completed exports and trigger alerts
    useEffect(() => {
        if (!data || data.length === 0) {
            previousDataRef.current = data || [];
            return;
        }

        // If this is the first load, just store the data without notifications
        if (previousDataRef.current.length === 0) {
            previousDataRef.current = data;
            return;
        }

        const previousData = previousDataRef.current;

        // Check for new exports that completed immediately
        if (data.length > previousData.length) {
            // Find exports that are in current data but not in previous data
            const newExports = data.filter(
                (currentExport) =>
                    !previousData.some(
                        (prev) =>
                            prev.transactionId === currentExport.transactionId
                    )
            );

            newExports.forEach((newExport) => {
                // If the new export is already completed (not running)
                if (newExport.status !== ProcessExportStatusEnum.NUMBER_1) {
                    onExportComplete?.(newExport.exportFileName || 'Export');
                }
            });
        }

        data.forEach((currentExport) => {
            const exportId = currentExport.transactionId || '';

            const previousExport = previousData.find(
                (prev) => prev.transactionId === exportId
            );

            if (
                previousExport &&
                previousExport.transactionId === currentExport.transactionId &&
                previousExport.status === ProcessExportStatusEnum.NUMBER_1 &&
                currentExport.status !== ProcessExportStatusEnum.NUMBER_1
            ) {
                onExportComplete?.(currentExport.exportFileName || 'Export');
            }
        });

        previousDataRef.current = data;
    }, [data, onExportComplete]);

    useEffect(() => {
        if (!data) return;

        const hasInProgress = data.some(
            (item) => item.status === ProcessExportStatusEnum.NUMBER_1
        );

        if (hasInProgress && !shouldPoll) {
            exportPollingController.start();
        } else if (!hasInProgress && shouldPoll) {
            // Don't auto-stop if polling was manually started recently (within 10 seconds)
            const timeSinceManualStart =
                Date.now() - manualStartTimeRef.current;
            const minPollingDuration = 10000; // 10 seconds

            if (timeSinceManualStart > minPollingDuration) {
                exportPollingController.stop();
            }
        }
    }, [data, shouldPoll]);

    return {
        data,
        refetch,
        isPolling: shouldPoll,
        startPolling: () => exportPollingController.start(),
        stopPolling: () => exportPollingController.stop(),
    };
}
